import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, Input, Select, Skeleton, useToast } from '../../design-system';
import '../styles/ChecklistTemplates.css';

// The firm's standard steps for a compliance head. Every task the proposer
// generates for that head starts with these ticked off empty.
//
// The steps are COPIED onto each task at creation, never referenced — so
// editing this changes future tasks only, and ticking an item on one client's
// filing can't tick it on everyone else's. The card says so, because "will this
// rewrite the 40 tasks already open?" is the first thing anyone asks.
function ChecklistTemplateManager() {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient
      .get('/api/compliance-types')
      .then((res) => setTypes(res.data || []))
      .catch(() => toast.danger('Failed to load compliance types'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async (id) => {
    if (!id) { setData(null); setItems([]); return; }
    try {
      setLoading(true);
      const res = await apiClient.get(`/api/compliance-types/${id}/checklist-template`);
      setData(res.data);
      setItems((res.data.effective || []).map((i) => i.label));
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load the template');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(selectedId); }, [selectedId, load]);

  // Grouped by category so the picker reads the way the compliance catalog does.
  const grouped = useMemo(() => {
    const map = new Map();
    types.forEach((t) => {
      if (!map.has(t.category)) map.set(t.category, []);
      map.get(t.category).push(t);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [types]);

  const addItem = () => {
    const label = draft.trim();
    if (!label) return;
    setItems((prev) => [...prev, label]);
    setDraft('');
  };
  const removeItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));
  const move = (index, delta) => {
    setItems((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put(`/api/compliance-types/${selectedId}/checklist-template`, {
        items: items.map((label, order) => ({ label, order })),
      });
      setData((prev) => ({ ...prev, ...res.data }));
      toast.success('Checklist saved — applies to tasks created from now on');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Discard your firm’s custom steps and use the catalog default?')) return;
    try {
      const res = await apiClient.delete(`/api/compliance-types/${selectedId}/checklist-template`);
      setData((prev) => ({ ...prev, ...res.data }));
      setItems((res.data.effective || []).map((i) => i.label));
      toast.success('Reverted to the catalog default');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to reset');
    }
  };

  return (
    <Card padding="md" className="cl-template-card">
      <div className="cl-head">
        <div>
          <h3 className="cl-title">Checklist templates</h3>
          <p className="cl-sub">
            Standard steps for a compliance head. Copied onto each task when it&apos;s created —
            editing here affects <strong>future tasks only</strong>.
          </p>
        </div>
      </div>

      <Select
        label="Compliance head"
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
      >
        <option value="">— pick a head —</option>
        {grouped.map(([category, list]) => (
          <optgroup key={category} label={category.replace(/_/g, ' ').toUpperCase()}>
            {list.map((t) => (
              <option key={t._id} value={t._id}>{t.name}</option>
            ))}
          </optgroup>
        ))}
      </Select>

      {loading && <Skeleton.Text lines={4} />}

      {!loading && selectedId && data && (
        <div className="cl-body">
          <div className="cl-status">
            {data.hasOverride ? (
              <span className="cl-badge cl-badge-custom">Your firm&apos;s own steps</span>
            ) : (
              <span className="cl-badge">Catalog default{data.catalogDefault?.length ? '' : ' (empty)'}</span>
            )}
            {data.hasOverride && (
              <button type="button" className="cl-reset" onClick={resetToDefault}>
                Reset to catalog default
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="cl-empty">
              No steps yet. Add the checks this filing always runs through — they&apos;ll appear on
              every task generated for this head.
            </p>
          ) : (
            <ol className="cl-list">
              {items.map((label, i) => (
                <li key={`${label}-${i}`} className="cl-item">
                  <span className="cl-item-label">{label}</span>
                  <span className="cl-item-actions">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down">↓</button>
                    <button type="button" className="cl-item-remove" onClick={() => removeItem(i)} aria-label="Remove step">×</button>
                  </span>
                </li>
              ))}
            </ol>
          )}

          <div className="cl-add">
            <Input
              placeholder="e.g. Reconcile GSTR-2B with purchase register"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
            />
            <Button variant="secondary" size="sm" onClick={addItem}>Add step</Button>
          </div>

          <div className="cl-footer">
            {/* Saving an EMPTY list is meaningful: it means "this head has no
                checklist" and suppresses the catalog default. Reset is the way
                back to the default. */}
            <Button variant="primary" onClick={save} loading={saving}>
              Save {items.length} step{items.length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default ChecklistTemplateManager;
